import { useEffect, useState } from 'react';
import { ArrowRight, CircleAlert, LoaderCircle } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { companyAiService } from '@/services/companyAiService';

function positiveNumber(value: string | null): number | null {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function stringValue(payload: Record<string, unknown> | undefined, key: string): string {
  return String(payload?.[key] || '').trim();
}

export default function OutlookInvoiceHandoffPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const params = new URLSearchParams(location.search);
    const emailMessageId = positiveNumber(params.get('emailMessageId'));
    if (!emailMessageId) {
      setError('A valid Outlook email reference is required.');
      return () => { active = false; };
    }

    void Promise.all([
      companyAiService.getEmailReviewItem(emailMessageId),
      companyAiService.getEmailDraftAssistance(emailMessageId),
    ]).then(([detail, assistance]) => {
      if (!active) return;
      const handoff = assistance.workflowHandoffs.find((item) => item.handoffType === 'invoice_follow_up_draft');
      const acceptedProject = detail.projectSuggestions.find((item) => item.reviewStatus === 'accepted')
        || detail.projectSuggestions[0]
        || null;
      const projectId = positiveNumber(params.get('projectId'))
        || positiveNumber(String(handoff?.projectId || ''))
        || acceptedProject?.projectId
        || null;
      const taskId = positiveNumber(params.get('taskId'));
      const rfiTicketNumber = String(params.get('rfiTicketNumber') || '').trim();
      const pricingAmount = positiveNumber(params.get('pricingAmount'));
      const description = stringValue(handoff?.payload, 'subject')
        || detail.message.subject
        || handoff?.title
        || 'Email follow-up';

      navigate('/invoices/workflow', {
        replace: true,
        state: {
          mode: 'create',
          prefill: {
            projectId: projectId ? String(projectId) : undefined,
            taskId: taskId ? String(taskId) : undefined,
            rfiTicketNumber: rfiTicketNumber || undefined,
            clientName: stringValue(handoff?.payload, 'clientName') || detail.message.fromName || '',
            clientEmail: stringValue(handoff?.payload, 'senderEmail') || detail.message.fromEmail || '',
            notes: stringValue(handoff?.payload, 'summary') || assistance.internalSummary || detail.message.bodyPreview || '',
            items: [{
              categoryName: rfiTicketNumber ? 'RFI' : 'Email follow-up',
              description,
              quantity: '1',
              unitPrice: pricingAmount ? String(pricingAmount) : '0',
              taxRate: '0.1',
            }],
            successMessage: `Loaded Outlook email ${emailMessageId}. PMapp has reloaded the live project and invoice context; review all values before saving.`,
          },
        },
      });
    }).catch((cause) => {
      if (active) setError(cause instanceof Error ? cause.message : 'Unable to prepare the invoice workflow.');
    });

    return () => { active = false; };
  }, [location.search, navigate]);

  return (
    <main style={{ minHeight: '70vh', display: 'grid', placeItems: 'center', padding: '32px' }}>
      <section style={{ width: 'min(520px, 100%)', padding: '28px', border: '1px solid #d8e0ee', borderRadius: '18px', background: '#fff', boxShadow: '0 18px 50px rgba(18, 43, 78, 0.12)' }}>
        {error ? (
          <>
            <CircleAlert size={28} color="#a33b32" />
            <h1 style={{ margin: '16px 0 8px' }}>Invoice handoff needs attention</h1>
            <p style={{ color: '#657187' }}>{error}</p>
            <button type="button" onClick={() => navigate('/ai/email')} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 14px' }}>
              Open email review <ArrowRight size={16} />
            </button>
          </>
        ) : (
          <>
            <LoaderCircle size={28} color="#2456c8" style={{ animation: 'spin 0.8s linear infinite' }} />
            <h1 style={{ margin: '16px 0 8px' }}>Preparing live invoice context</h1>
            <p style={{ margin: 0, color: '#657187' }}>PMapp is loading the project, email evidence, RFI references, and current invoice workflow.</p>
          </>
        )}
      </section>
    </main>
  );
}
